// Temporary OFF path from main 072fa6b8c.
'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type {
  DetectImportKindResponse,
  ImportKind,
  RunImportResponse,
} from '@borradh-workspace/api-client/types';
import {
  importKindLabels,
  importKindValues,
} from '@borradh-workspace/api-client/types';
import { FileSpreadsheetIcon, Loader2Icon, UploadIcon } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  ImportOptions,
  Importer,
  SpreadsheetUpload,
} from '../api/importer';

const MAX_FILE_BYTES = 10_485_760; // 10 MB — every import-csv endpoint's cap
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const isXlsx = (f: File) =>
  f.name.toLowerCase().endsWith('.xlsx') || f.type === XLSX_MIME;
const isCsv = (f: File) =>
  f.name.toLowerCase().endsWith('.csv') || f.type === 'text/csv';

const fileToBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(f);
  });

interface KindOption {
  id: string;
  label: ReactNode;
  defaultChecked?: boolean;
}

/** What each kind is called on the button, and what it asks before importing. */
const KIND_CONFIG: Record<ImportKind, { noun: string; options: KindOption[] }> =
  {
    customers: {
      noun: 'customers',
      options: [
        {
          id: 'updateExisting',
          label: 'Update customers that already exist (matched by email).',
        },
        {
          id: 'customersAreClients',
          label:
            'These are existing clients, not new leads — list them under Clients.',
          defaultChecked: true,
        },
        {
          id: 'consentAcknowledgment',
          label:
            'I confirm this organization is allowed to store these customer records.',
        },
        {
          id: 'defaultConsentEmail',
          label: 'Record email marketing consent for every imported customer.',
        },
        {
          id: 'defaultConsentWhatsapp',
          label:
            'Record WhatsApp marketing consent for every imported customer.',
        },
        {
          id: 'defaultConsentSms',
          label: 'Record SMS marketing consent for every imported customer.',
        },
        {
          id: 'defaultConsentVoice',
          label: 'Record voice-call consent for every imported customer.',
        },
      ],
    },
    teamMembers: {
      noun: 'team members',
      options: [
        {
          id: 'updateExisting',
          label: 'Update team members that already exist (matched by email).',
        },
      ],
    },
    services: {
      noun: 'services',
      options: [
        { id: 'updateExisting', label: 'Update services that already exist.' },
        {
          id: 'syncCategories',
          label:
            'Create the categories this file uses and file every service under them, including services already in Borrah (only their category changes).',
          defaultChecked: true,
        },
        {
          id: 'importAsInactive',
          label:
            'Import as archived so I can review them before they’re bookable.',
        },
      ],
    },
    products: {
      noun: 'products',
      options: [
        { id: 'updateExisting', label: 'Update products that already exist.' },
        {
          id: 'importAsNonRetail',
          label:
            'Import without putting anything on sale, so I can price them first.',
        },
      ],
    },
    membershipPlans: {
      noun: 'plans',
      options: [
        { id: 'updateExisting', label: 'Update plans that already exist.' },
        {
          id: 'importAsInactive',
          label:
            'Import as inactive so I can review them before they go on sale.',
        },
      ],
    },
    offers: {
      noun: 'promotions',
      options: [
        {
          id: 'updateExisting',
          label:
            'Update promotions that already exist (same code, or same name).',
        },
        {
          id: 'importAsDraft',
          label: 'Import as drafts so I can review them first.',
        },
      ],
    },
    resources: {
      noun: 'rooms & equipment',
      options: [
        {
          id: 'updateExisting',
          label: 'Update rooms and equipment that already exist.',
        },
        {
          id: 'importAsInactive',
          label:
            'Import as inactive so I can review them before bookings use them.',
        },
      ],
    },
    shifts: {
      noun: 'shifts',
      options: [],
    },
    calendar: {
      noun: 'bookings',
      options: [
        {
          id: 'updateExisting',
          label: 'Replace bookings that already exist at the same time.',
        },
        {
          id: 'createMissingCustomers',
          label:
            'Create customers that aren’t in Borrah yet, from the name, email and phone in the file.',
          defaultChecked: true,
        },
        {
          id: 'consentAcknowledgment',
          label:
            'These are my existing clients and I’m allowed to store their contact details.',
        },
      ],
    },
  };

const defaults = (kind: ImportKind | null) =>
  Object.fromEntries(
    (kind ? KIND_CONFIG[kind].options : []).map((o) => [
      o.id,
      o.defaultChecked ?? false,
    ])
  );

export interface ImportDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The page's own kind: preselected when the file doesn't say clearly. */
  kind?: ImportKind;
  importer: Importer;
  onComplete?: (report: ImportRunReport) => void;
}

export interface ImportRunReport {
  id: string;
  fileName: string;
  upload: SpreadsheetUpload;
  headers: string[];
  options: Partial<Record<ImportKind, ImportOptions>>;
  results: RunImportResponse[];
}

const isRunImportResponse = (value: unknown): value is RunImportResponse => {
  const result = value as Partial<RunImportResponse> | null;
  return Boolean(
    result &&
      typeof result.kind === 'string' &&
      typeof result.imported === 'number' &&
      Array.isArray(result.errors)
  );
};

interface ImportFile {
  id: string;
  upload: SpreadsheetUpload;
  detected: DetectImportKindResponse;
  kinds: ImportKind[];
  options: Partial<Record<ImportKind, ImportOptions>>;
  failedKinds: ImportKind[];
  failureMessages: Partial<Record<ImportKind, string>>;
}

/** Each file keeps its own detection, options and retry state. */
export function ImportDataDialog({
  open,
  onOpenChange,
  kind: pageKind,
  importer,
  onComplete,
}: ImportDataDialogProps) {
  const [files, setFiles] = useState<ImportFile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const busyRef = useRef(false);
  const [runningKind, setRunningKind] = useState<ImportKind | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const active = files.find((file) => file.id === activeId) ?? files[0];
  const {
    upload,
    detected,
    kinds = [],
    options = {},
    failedKinds = [],
    failureMessages = {},
  } = active ?? {};
  const isDetecting = reading || importer.isDetecting;
  const isImporting = importer.isImporting || runningKind !== null;
  const availableKinds = importer.supportedKinds ?? importKindValues;
  const updateActive = (update: (file: ImportFile) => ImportFile) => {
    setFiles((previous) =>
      previous.map((file) => (file.id === active?.id ? update(file) : file))
    );
  };
  const toggleKind = (next: ImportKind, checked: boolean) => {
    updateActive((file) => ({
      ...file,
      kinds: checked
        ? [...file.kinds, next]
        : file.kinds.filter((kind) => kind !== next),
    }));
  };
  const setOptions = (
    update: (options: ImportFile['options']) => ImportFile['options']
  ) => {
    updateActive((file) => ({ ...file, options: update(file.options) }));
  };

  const acceptFiles = async (incoming: File[]) => {
    if (busyRef.current || isImporting || isDetecting) return;
    busyRef.current = true;
    setReading(true);
    try {
      for (const f of incoming) {
        if (f.name.toLowerCase().endsWith('.xls') && !isXlsx(f)) {
          toast.error(
            `${f.name}: Legacy .xls isn’t supported — save it as .xlsx or .csv`
          );
          continue;
        }
        if (!(isCsv(f) || isXlsx(f))) {
          toast.error(`${f.name}: Choose a .csv or .xlsx file`);
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          toast.error(
            `${f.name}: File is over 10 MB. Split it and import in parts.`
          );
          continue;
        }
        try {
          const payload: SpreadsheetUpload = isXlsx(f)
            ? { fileBase64: await fileToBase64(f), fileName: f.name }
            : { csv: await f.text(), fileName: f.name };
          const result = await importer.detect(payload);
          const suggested = result.kind ?? pageKind;
          const selected: ImportKind[] = [
            suggested && availableKinds.includes(suggested)
              ? suggested
              : (availableKinds[0] ?? 'services'),
          ];
          const teamScore = result.candidates.find(
            (candidate) => candidate.kind === 'teamMembers'
          )?.score;
          if (
            result.kind === 'shifts' &&
            availableKinds.includes('teamMembers') &&
            (teamScore ?? 0) >= 8
          ) {
            selected.unshift('teamMembers');
          }
          const id = crypto.randomUUID();
          setFiles((previous) => [
            ...previous,
            {
              id,
              upload: payload,
              detected: result,
              kinds: [...new Set(selected)],
              options: {},
              failedKinds: [],
              failureMessages: {},
            },
          ]);
          setActiveId((previous) => previous ?? id);
        } catch (error) {
          toast.error(
            `${f.name}: ${error instanceof Error ? error.message : 'Could not read the file'}`
          );
        }
      }
    } finally {
      setReading(false);
      busyRef.current = false;
    }
  };

  const submit = async () => {
    if (busyRef.current || isImporting || isDetecting) return;
    const pending = files.filter((file) => file.kinds.length > 0);
    if (!pending.length) return;
    // Validate every file before the first write.
    for (const file of pending) {
      const calendar = { ...defaults('calendar'), ...file.options.calendar };
      const customers = { ...defaults('customers'), ...file.options.customers };
      if (
        file.kinds.includes('calendar') &&
        calendar.createMissingCustomers &&
        !calendar.consentAcknowledgment
      ) {
        setActiveId(file.id);
        toast.error(
          `${file.upload.fileName}: Tick the consent box to create customers from the file, or turn customer creation off.`
        );
        return;
      }
      if (
        file.kinds.includes('customers') &&
        !customers.consentAcknowledgment
      ) {
        setActiveId(file.id);
        toast.error(
          `${file.upload.fileName}: Confirm that this organization is allowed to store the customer records.`
        );
        return;
      }
    }
    busyRef.current = true;
    const outcomes = new Map(
      pending.map((file) => [
        file.id,
        {
          completed: [] as RunImportResponse[],
          failed: [] as ImportKind[],
          messages: {} as Partial<Record<ImportKind, string>>,
        },
      ])
    );
    // Run dependencies across ALL files: staff before shifts, catalogue before calendar.
    for (const kind of availableKinds) {
      for (const file of pending.filter((file) => file.kinds.includes(kind))) {
        setRunningKind(kind);
        setActiveId(file.id);
        const outcome = outcomes.get(file.id);
        if (!outcome) continue;
        try {
          const result = await importer.run(kind, file.upload, {
            ...defaults(kind),
            ...file.options[kind],
          });
          if (isRunImportResponse(result)) outcome.completed.push(result);
        } catch (error) {
          outcome.failed.push(kind);
          outcome.messages[kind] =
            error instanceof Error ? error.message : 'Import failed';
        }
      }
    }
    setRunningKind(null);
    busyRef.current = false;
    setFiles((previous) =>
      previous.map((file) => {
        const outcome = outcomes.get(file.id);
        return outcome
          ? {
              ...file,
              kinds: outcome.failed,
              failedKinds: outcome.failed,
              failureMessages: outcome.messages,
            }
          : file;
      })
    );
    for (const file of pending) {
      const outcome = outcomes.get(file.id);
      if (outcome?.completed.length) {
        onComplete?.({
          id: file.id,
          fileName: file.upload.fileName,
          upload: file.upload,
          headers: file.detected.headers,
          // The options each import actually ran with, defaults included, so
          // a retry from the report behaves like the first run.
          options: Object.fromEntries(
            outcome.completed.map((result) => [
              result.kind,
              { ...defaults(result.kind), ...file.options[result.kind] },
            ])
          ),
          results: outcome.completed,
        });
      }
    }
    const firstFailed = pending.find(
      (file) => outcomes.get(file.id)?.failed.length
    );
    if (firstFailed) setActiveId(firstFailed.id);
    else onOpenChange(false);
  };

  const pendingFiles = files.filter((file) => file.kinds.length > 0);
  const canSubmit = pendingFiles.length > 0 && !isDetecting && !isImporting;

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!isImporting && !isDetecting) onOpenChange(next);
      }}
      open={open}
    >
      <DialogContent className="flex max-h-[min(90dvh,56rem)] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 border-b px-6 py-5 pr-12 text-left">
          <DialogTitle>Import from files</DialogTitle>
          <DialogDescription>
            Drop an export from your previous system — customers, services,
            products, membership plans, promotions, rooms, team members, shifts
            or a calendar. The columns are identified automatically, whatever
            they’re called. CSV or Excel, up to 10 MB per file.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-6 [overflow-wrap:anywhere]">
          <button
            className={cn(
              'flex w-full flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center transition-colors',
              dragOver ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
            )}
            disabled={isImporting || isDetecting}
            onClick={() => inputRef.current?.click()}
            onDragLeave={() => setDragOver(false)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void acceptFiles(Array.from(e.dataTransfer.files));
            }}
            type="button"
          >
            <UploadIcon className="size-8 text-muted-foreground" />
            <span className="font-medium">
              {isDetecting
                ? 'Reading the columns…'
                : 'Drop your CSV or Excel files here or click to browse'}
            </span>
            <span className="text-muted-foreground text-xs">
              .csv or .xlsx up to 10 MB each
            </span>
          </button>
          <input
            accept={`.csv,text/csv,.xlsx,${XLSX_MIME}`}
            className="hidden"
            onChange={(e) => {
              void acceptFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
            ref={inputRef}
            multiple
            aria-label="CSV or Excel files"
            type="file"
          />

          {files.length > 0 && (
            <div className="space-y-2" aria-label="Selected files">
              {files.map((file) => (
                <div key={file.id} className="flex items-center gap-2">
                  <Button
                    className="min-w-0 flex-1 justify-start"
                    variant={file.id === active?.id ? 'secondary' : 'outline'}
                    disabled={isImporting || isDetecting}
                    onClick={() => setActiveId(file.id)}
                  >
                    <FileSpreadsheetIcon className="size-4 shrink-0" />
                    <span className="truncate">{file.upload.fileName}</span>
                    {file.failedKinds.length > 0 && <span>Retry needed</span>}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={isImporting || isDetecting}
                    aria-label={`Remove ${file.upload.fileName}`}
                    onClick={() =>
                      setFiles((previous) =>
                        previous.filter((item) => item.id !== file.id)
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}

          {detected && (
            <div className="flex flex-col gap-4">
              <p className="text-sm">
                <strong>{upload?.fileName}</strong> · {detected.rowsInFile} rows
                · {detected.headers.slice(0, 5).join(', ')}
              </p>
              <fieldset
                disabled={isImporting || isDetecting}
                className="min-w-0 space-y-2"
              >
                <legend className="font-medium text-sm">This looks like</legend>
                <p className="text-muted-foreground text-xs">
                  Select everything you want to import from this file.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {availableKinds.map((kind) => (
                    <label
                      className="flex items-center gap-2 text-sm"
                      htmlFor={`import-kind-${kind}`}
                      key={kind}
                    >
                      <Checkbox
                        id={`import-kind-${kind}`}
                        checked={kinds.includes(kind)}
                        onCheckedChange={(checked) =>
                          toggleKind(kind, Boolean(checked))
                        }
                        disabled={isImporting || isDetecting}
                      />
                      {importKindLabels[kind]}
                    </label>
                  ))}
                </div>
              </fieldset>
              {availableKinds
                .filter(
                  (kind) =>
                    kinds.includes(kind) && KIND_CONFIG[kind].options.length > 0
                )
                .map((kind) => (
                  <fieldset
                    className="min-w-0 space-y-2 rounded-md border p-3"
                    disabled={isImporting || isDetecting}
                    key={kind}
                  >
                    <legend className="px-1 font-medium text-sm">
                      {importKindLabels[kind]}
                    </legend>
                    {KIND_CONFIG[kind].options.map((option) => {
                      const id = `import-option-${kind}-${option.id}`;
                      return (
                        <label
                          className="flex items-start gap-2 text-sm"
                          htmlFor={id}
                          key={option.id}
                        >
                          <Checkbox
                            checked={
                              options[kind]?.[option.id] ??
                              option.defaultChecked ??
                              false
                            }
                            className="mt-0.5"
                            disabled={isImporting || isDetecting}
                            id={id}
                            onCheckedChange={(checked) =>
                              setOptions((previous) => ({
                                ...previous,
                                [kind]: {
                                  ...previous[kind],
                                  [option.id]: Boolean(checked),
                                },
                              }))
                            }
                          />
                          {option.label}
                        </label>
                      );
                    })}
                  </fieldset>
                ))}
              {failedKinds.length > 0 && (
                <div
                  role="alert"
                  className="space-y-1 text-destructive text-sm"
                >
                  <p>
                    Completed types have been deselected. Fix or split the file,
                    then retry:
                  </p>
                  <ul className="list-disc pl-5">
                    {failedKinds.map((kind) => (
                      <li key={kind}>
                        {importKindLabels[kind]}:{' '}
                        {failureMessages[kind] ?? 'Import failed'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t px-6 py-4">
          <Button
            disabled={isImporting || isDetecting}
            onClick={() => onOpenChange(false)}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit} type="button">
            {isImporting ? (
              <>
                <Loader2Icon className="size-4 animate-spin" />
                Importing {runningKind ? importKindLabels[runningKind] : ''}…
              </>
            ) : pendingFiles.length > 1 ? (
              `Import ${pendingFiles.length} files`
            ) : kinds.length > 1 ? (
              `Import ${kinds.length} types`
            ) : (
              `Import ${kinds[0] ? KIND_CONFIG[kinds[0]].noun : ''}`.trim()
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
